import { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../../../../environment";
import { getAuthHeaders } from "../../../../utils/authUtils";
import { cachedRequest, invalidateCachedRequest } from "../../../../utils/requestCache";
import { useFilterOptions } from "../../../../hooks/useFilterOptions";
import { currencyOptions } from "../../../../components/currency";
import { LifecycleService } from "../../../crm/leads/lifecycle.service";
import { LifecycleStatusReasonService } from "../../../settings/lifecycleStatusReason/lifecycleStatusReason.service";
import { FormLookupsService } from "../../../../components/UniversalForm/formLookups.service";
import type { LifecycleLabels } from "../../../../utils/lifecycleUtils";
import { BASE_STATUS_OPTIONS, FilterOption, FilterOptionGroup, PRIORITY_OPTIONS } from "./summaryFilters";

const STAGE_LIFECYCLES: { key: string; label: (names: LifecycleLabels) => string }[] = [
  { key: "LEAD", label: (names) => names.lead },
  { key: "PIPELINE", label: (names) => names.pipeline },
  { key: "DEAL", label: (names) => names.deal },
];

const PRODUCT_PAGE_SIZE = 200;

interface StageGroupData {
  key: string;
  stages: { id: number; name: string }[];
}

const loadStageGroups = async (tenantId: number): Promise<StageGroupData[]> => {
  const lifecycleTypes = await LifecycleService.getLifecycleTypes(tenantId);
  const groups = await Promise.all(
    STAGE_LIFECYCLES.map(async ({ key }) => {
      const type = (lifecycleTypes || []).find((item) => item.lifecycle_type_key === key);
      if (!type) return { key, stages: [] };
      const stages = await LifecycleService.getLifecycleStages(type.lifecycle_type_id, tenantId);
      return {
        key,
        stages: (stages || []).map((stage) => ({ id: stage.lifecycle_type_stage_id, name: stage.stage_name })),
      };
    })
  );
  return groups;
};

interface StatusReasonData {
  id: string;
  name: string;
  status: string;
}

const loadStatusReasons = (tenantId: number) =>
  cachedRequest(`summary-status-reason-options:${tenantId}`, async (): Promise<StatusReasonData[]> => {
    const [reasons, categories] = await Promise.all([
      LifecycleStatusReasonService.getReasons(tenantId),
      LifecycleStatusReasonService.getCategories(),
    ]);
    const categoryKeys = new Map(
      (categories || []).map((category) => [
        category.lifecycle_status_reason_category_id,
        String(category.lifecycle_status_reason_category_key || "").toLowerCase(),
      ])
    );
    return (reasons || [])
      .filter((reason) => reason.is_active !== false && reason.lifecycle_status_reason_id && reason.reason_name)
      .map((reason: any) => ({
        id: String(reason.lifecycle_status_reason_id),
        name: String(reason.reason_name),
        status:
          categoryKeys.get(reason.lifecycle_status_reason_category_id) ||
          String(reason.lifecycle_status_reason_category?.lifecycle_status_reason_category_key || "").toLowerCase(),
      }));
  });

const loadProducts = (tenantId: number) =>
  cachedRequest(`summary-products:${tenantId}`, async () => {
    const params = new URLSearchParams({
      tenant_id: String(tenantId),
      page: "1",
      page_size: String(PRODUCT_PAGE_SIZE),
      is_active: "true",
      response_mode: "light",
    });
    const response = await fetch(`${API_BASE_URL}/product-n-service/?${params.toString()}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(`Failed to load products (${response.status})`);
    const data = await response.json();
    const results: any[] = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
    return results
      .filter((item) => item?.product_n_service_id)
      .map((item) => ({
        value: String(item.product_n_service_id),
        label: String(item.product_n_service_name || `#${item.product_n_service_id}`),
      }));
  });

const toOptions = (items: { value: number; label: string }[]): FilterOption[] =>
  items.filter((item) => item.value !== undefined && item.value !== null).map((item) => ({
    value: String(item.value),
    label: item.label || String(item.value),
  }));

export const CURRENCY_FILTER_OPTIONS: FilterOption[] = currencyOptions.map((option) => ({
  value: String(option.value),
  label: String(option.label),
}));

export const useSummaryFilterOptions = (
  tenantId: string | null,
  names: LifecycleLabels,
  enabled: boolean,
  isOpen: boolean
) => {
  const numericTenantId = Number(tenantId);
  const [stageGroups, setStageGroups] = useState<StageGroupData[]>([]);
  const [reasons, setReasons] = useState<StatusReasonData[]>([]);
  const [products, setProducts] = useState<FilterOption[]>([]);
  const [sources, setSources] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState({ stages: false, reasons: false, products: false, sources: false });

  const { teams, users, isLoading } = useFilterOptions(tenantId, { teams: true, users: true }, enabled);

  useEffect(() => {
    if (!enabled || !numericTenantId) return;
    let cancelled = false;
    const track = <T,>(key: keyof typeof loading, request: Promise<T>, apply: (value: T) => void) => {
      setLoading((prev) => ({ ...prev, [key]: true }));
      request
        .then((value) => {
          if (!cancelled) apply(value);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setLoading((prev) => ({ ...prev, [key]: false }));
        });
    };
    track("stages", loadStageGroups(numericTenantId), setStageGroups);
    track("reasons", loadStatusReasons(numericTenantId), setReasons);
    track("products", loadProducts(numericTenantId), setProducts);
    return () => {
      cancelled = true;
    };
  }, [enabled, numericTenantId]);

  useEffect(() => {
    if (!enabled || !isOpen || !numericTenantId) return;
    let cancelled = false;
    setLoading((prev) => ({ ...prev, sources: true }));
    invalidateCachedRequest(`lifecycle-sources:${numericTenantId}`);
    FormLookupsService.fetchLifecycleSources(String(numericTenantId))
      .then((items) => {
        if (cancelled) return;
        setSources(
          items
            .filter((item) => item.value !== undefined && item.value !== null)
            .map((item) => ({ value: String(item.value), label: String(item.label || item.value) }))
        );
      })
      .finally(() => {
        if (!cancelled) setLoading((prev) => ({ ...prev, sources: false }));
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, isOpen, numericTenantId]);

  const stageOptions = useMemo<FilterOptionGroup[]>(
    () =>
      stageGroups
        .filter((group) => group.stages.length)
        .map((group) => {
          const lifecycle = STAGE_LIFECYCLES.find((item) => item.key === group.key);
          const groupLabel = lifecycle ? lifecycle.label(names) : group.key;
          return {
            label: groupLabel,
            options: group.stages.map((stage) => ({
              value: String(stage.id),
              label: `${groupLabel} · ${stage.name}`,
              group: group.key,
            })),
          };
        }),
    [stageGroups, names]
  );

  const lifecycleOptions = useMemo<FilterOption[]>(
    () =>
      STAGE_LIFECYCLES.map(({ key, label }) => ({
        value: key,
        label: label(names),
        stageIds: (stageGroups.find((group) => group.key === key)?.stages ?? []).map((stage) => String(stage.id)),
      })).filter((option) => !stageGroups.length || option.stageIds.length > 0),
    [stageGroups, names]
  );

  const reasonOptionsByStatus = useMemo<Record<string, FilterOption[]>>(
    () =>
      reasons.reduce<Record<string, FilterOption[]>>((result, reason) => {
        if (!reason.status) return result;
        return {
          ...result,
          [reason.status]: [...(result[reason.status] ?? []), { value: reason.id, label: reason.name, group: reason.status }],
        };
      }, {}),
    [reasons]
  );

  return {
    lifecycleOptions,
    stageOptions,
    statusOptions: BASE_STATUS_OPTIONS,
    reasonOptionsByStatus,
    teamOptions: useMemo(() => toOptions(teams), [teams]),
    userOptions: useMemo(() => toOptions(users), [users]),
    sourceOptions: sources,
    priorityOptions: PRIORITY_OPTIONS,
    productOptions: products,
    currencyOptions: CURRENCY_FILTER_OPTIONS,
    loading: {
      stages: loading.stages,
      reasons: loading.reasons,
      products: loading.products,
      teams: isLoading.teams,
      users: isLoading.users,
      sources: loading.sources,
    },
  };
};
